import React from 'react';
import { BarChart3, Monitor, FileText, List, Calendar, PenTool, CheckCircle2 } from 'lucide-react';
import { HeroSection } from '../components/HeroSection';
import { BlogSection } from '../components/BlogSection';
interface HomePageProps {
  onNavigate: (page: string) => void;
}
export function HomePage({
  onNavigate
}: HomePageProps) {
  return <div className="min-h-screen bg-sp-light dark:bg-slate-950 transition-colors duration-300" data-id="element-645">
      {/* Hero Section */}
      <HeroSection title="Das Smart Protect Prinzip. Einfach & Transparent" subtitle="Erst vergleichen, dann verstehen – und wenn du willst: persönlich einordnen lassen. Klar, digital, ohne Druck." primaryCtaText="Jetzt vergleichen" secondaryCtaText="Beratung buchen" onPrimaryClick={() => onNavigate('rechner')} onSecondaryClick={() => onNavigate('beratung')} benefits={['Unabhängiger Vergleich', 'Persönliche Beratung', '100% Digital']} data-id="element-646" />

      {/* 3 Schritte Section */}
      <section className="py-16 md:py-20 px-4 md:px-6 bg-white dark:bg-slate-900 transition-colors duration-300 relative overflow-hidden" data-id="element-647">
        {/* Subtle background decoration */}
        <div className="absolute inset-0 -z-10" data-id="element-648">
          <div className="absolute top-0 left-1/4 h-64 w-64 rounded-full bg-blue-100/40 dark:bg-blue-900/10 blur-3xl" data-id="element-649"></div>
          <div className="absolute bottom-0 right-1/4 h-64 w-64 rounded-full bg-sky-100/40 dark:bg-sky-900/10 blur-3xl" data-id="element-650"></div>
        </div>

        <div className="container mx-auto max-w-5xl" data-id="element-651">
          {/* Enhanced Header */}
          <div className="text-center mb-12" data-id="element-652">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm px-4 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 border border-blue-100 dark:border-blue-900/50 shadow-sm mb-4" data-id="element-653">
              <div className="h-1.5 w-1.5 rounded-full bg-blue-500 animate-pulse" data-id="element-654"></div>
              <span data-id="element-655">Einfacher Prozess</span>
            </div>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-slate-900 dark:text-white mb-3" data-id="element-656">
              So läuft's ab
            </h2>
            <p className="text-slate-600 dark:text-slate-400 text-base md:text-lg max-w-2xl mx-auto" data-id="element-657">
              Digital starten, sauber vergleichen – und wenn du willst:
              persönlich einordnen lassen.
            </p>
          </div>

          {/* Timeline Progress Indicator (Desktop) */}
          <div className="hidden md:flex items-center justify-center mb-8" data-id="element-658">
            <div className="flex items-center gap-0" data-id="element-659">
              <div className="h-10 w-10 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold shadow-lg shadow-blue-500/30" data-id="element-660">
                1
              </div>
              <div className="h-1 w-24 lg:w-32 bg-gradient-to-r from-blue-600 to-blue-400" data-id="element-661"></div>
              <div className="h-10 w-10 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-bold shadow-lg shadow-blue-500/30" data-id="element-662">
                2
              </div>
              <div className="h-1 w-24 lg:w-32 bg-gradient-to-r from-blue-400 to-blue-300" data-id="element-663"></div>
              <div className="h-10 w-10 rounded-full bg-blue-400 text-white flex items-center justify-center text-sm font-bold shadow-lg shadow-blue-400/30" data-id="element-664">
                3
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8" data-id="element-665">
            {/* Step 1 */}
            <div className="group relative bg-white dark:bg-slate-900 rounded-2xl overflow-hidden shadow-lg shadow-slate-200/50 dark:shadow-none border border-slate-200/60 dark:border-slate-800 hover:shadow-2xl hover:shadow-blue-500/10 dark:hover:shadow-blue-500/5 hover:-translate-y-2 transition-all duration-500" data-id="element-666">
              {/* Colored accent bar at top */}
              <div className="h-1.5 bg-gradient-to-r from-blue-600 via-blue-500 to-sky-400" data-id="element-667"></div>

              {/* Decorative background number */}
              <div className="absolute top-4 right-4 text-[120px] font-black text-slate-100/50 dark:text-slate-800/30 leading-none select-none pointer-events-none group-hover:text-blue-100/60 dark:group-hover:text-blue-900/20 transition-colors duration-500" data-id="element-668">
                1
              </div>

              <div className="relative p-6" data-id="element-669">
                {/* Icon with gradient background */}
                <div className="mb-5 flex items-center gap-4" data-id="element-670">
                  <div className="relative" data-id="element-671">
                    <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/30 group-hover:shadow-blue-500/50 group-hover:scale-110 transition-all duration-300" data-id="element-672">
                      <BarChart3 className="h-7 w-7 text-white" data-id="element-673" />
                    </div>
                    <div className="absolute -bottom-1 -right-1 h-5 w-5 rounded-full bg-white dark:bg-slate-900 border-2 border-blue-500 flex items-center justify-center" data-id="element-674">
                      <span className="text-[10px] font-bold text-blue-600" data-id="element-675">
                        1
                      </span>
                    </div>
                  </div>
                  <div data-id="element-676">
                    <p className="text-xs font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wider mb-0.5" data-id="element-677">
                      Schritt 1
                    </p>
                    <h3 className="font-bold text-lg text-slate-900 dark:text-white" data-id="element-678">
                      Vergleichen
                    </h3>
                  </div>
                </div>

                {/* Mini UI Preview - Enhanced */}
                <div className="relative bg-gradient-to-br from-slate-50 via-white to-blue-50/30 dark:from-slate-800 dark:via-slate-800/80 dark:to-blue-900/10 rounded-xl p-4 mb-5 border border-slate-200 dark:border-slate-700 group-hover:border-blue-300 dark:group-hover:border-blue-800 transition-all duration-300" data-id="element-679">
                  <div className="flex items-center justify-between" data-id="element-680">
                    <div className="flex items-center gap-3" data-id="element-681">
                      <div className="h-8 w-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center" data-id="element-682">
                        <List className="h-4 w-4 text-blue-600 dark:text-blue-400" data-id="element-683" />
                      </div>
                      <div data-id="element-684">
                        <p className="text-[10px] text-slate-400 uppercase tracking-wide" data-id="element-685">
                          Tarife
                        </p>
                        <p className="text-xs font-medium text-slate-700 dark:text-slate-300" data-id="element-686">
                          12 Ergebnisse
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-1" data-id="element-687">
                      <div className="h-2 w-2 rounded-full bg-blue-500" data-id="element-688"></div>
                      <div className="h-2 w-2 rounded-full bg-sky-400" data-id="element-689"></div>
                      <div className="h-2 w-2 rounded-full bg-slate-300 dark:bg-slate-600" data-id="element-690"></div>
                    </div>
                  </div>
                  {/* Mini progress bars */}
                  <div className="mt-3 space-y-1.5" data-id="element-691">
                    <div className="h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden" data-id="element-692">
                      <div className="h-full w-4/5 bg-gradient-to-r from-blue-500 to-blue-400 rounded-full" data-id="element-693"></div>
                    </div>
                    <div className="h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden" data-id="element-694">
                      <div className="h-full w-3/5 bg-gradient-to-r from-blue-400 to-sky-400 rounded-full" data-id="element-695"></div>
                    </div>
                  </div>
                </div>

                <p className="text-slate-600 dark:text-slate-400 text-sm mb-5 leading-relaxed" data-id="element-696">
                  Wähle den passenden Rechner und gib deine Angaben ein. Ziel:
                  ein klares Ranking statt „alles sammeln".
                </p>

                <ul className="space-y-3 text-slate-600 dark:text-slate-400 text-sm" data-id="element-697">
                  <li className="flex items-start gap-3" data-id="element-698">
                    <div className="h-5 w-5 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-699">
                      <CheckCircle2 className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" data-id="element-700" />
                    </div>
                    <span data-id="element-701">Varianten testen (Selbstbeteiligung, Bausteine)</span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-702">
                    <div className="h-5 w-5 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-703">
                      <CheckCircle2 className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" data-id="element-704" />
                    </div>
                    <span data-id="element-705">Leistung zuerst – Beitrag danach feinjustieren</span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-706">
                    <div className="h-5 w-5 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-707">
                      <CheckCircle2 className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" data-id="element-708" />
                    </div>
                    <span data-id="element-709">Am Ende: 1–2 Favoriten, die wirklich passen</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Step 2 */}
            <div className="group relative bg-white dark:bg-slate-900 rounded-2xl overflow-hidden shadow-lg shadow-slate-200/50 dark:shadow-none border border-slate-200/60 dark:border-slate-800 hover:shadow-2xl hover:shadow-sky-500/10 dark:hover:shadow-sky-500/5 hover:-translate-y-2 transition-all duration-500" data-id="element-710">
              {/* Colored accent bar at top */}
              <div className="h-1.5 bg-gradient-to-r from-sky-500 via-sky-400 to-cyan-400" data-id="element-711"></div>

              {/* Decorative background number */}
              <div className="absolute top-4 right-4 text-[120px] font-black text-slate-100/50 dark:text-slate-800/30 leading-none select-none pointer-events-none group-hover:text-sky-100/60 dark:group-hover:text-sky-900/20 transition-colors duration-500" data-id="element-712">
                2
              </div>

              <div className="relative p-6" data-id="element-713">
                {/* Icon with gradient background */}
                <div className="mb-5 flex items-center gap-4" data-id="element-714">
                  <div className="relative" data-id="element-715">
                    <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-sky-500 to-sky-600 flex items-center justify-center shadow-lg shadow-sky-500/30 group-hover:shadow-sky-500/50 group-hover:scale-110 transition-all duration-300" data-id="element-716">
                      <Monitor className="h-7 w-7 text-white" data-id="element-717" />
                    </div>
                    <div className="absolute -bottom-1 -right-1 h-5 w-5 rounded-full bg-white dark:bg-slate-900 border-2 border-sky-500 flex items-center justify-center" data-id="element-718">
                      <span className="text-[10px] font-bold text-sky-600" data-id="element-719">
                        2
                      </span>
                    </div>
                  </div>
                  <div data-id="element-720">
                    <p className="text-xs font-medium text-sky-600 dark:text-sky-400 uppercase tracking-wider mb-0.5" data-id="element-721">
                      Schritt 2
                    </p>
                    <h3 className="font-bold text-lg text-slate-900 dark:text-white" data-id="element-722">
                      Einordnen
                    </h3>
                  </div>
                </div>

                {/* Mini UI Preview - Enhanced */}
                <div className="relative bg-gradient-to-br from-slate-50 via-white to-sky-50/30 dark:from-slate-800 dark:via-slate-800/80 dark:to-sky-900/10 rounded-xl p-4 mb-5 border border-slate-200 dark:border-slate-700 group-hover:border-sky-300 dark:group-hover:border-sky-800 transition-all duration-300" data-id="element-723">
                  <div className="flex items-center justify-between" data-id="element-724">
                    <div className="flex items-center gap-3" data-id="element-725">
                      <div className="h-8 w-8 rounded-lg bg-sky-100 dark:bg-sky-900/30 flex items-center justify-center" data-id="element-726">
                        <Calendar className="h-4 w-4 text-sky-600 dark:text-sky-400" data-id="element-727" />
                      </div>
                      <div data-id="element-728">
                        <p className="text-[10px] text-slate-400 uppercase tracking-wide" data-id="element-729">
                          Beratung
                        </p>
                        <p className="text-xs font-medium text-slate-700 dark:text-slate-300" data-id="element-730">
                          15 Min Call
                        </p>
                      </div>
                    </div>
                    <div className="h-6 w-6 rounded-full bg-sky-100 dark:bg-sky-900/30 flex items-center justify-center" data-id="element-731">
                      <CheckCircle2 className="h-3.5 w-3.5 text-sky-600 dark:text-sky-400" data-id="element-732" />
                    </div>
                  </div>
                  {/* Chat bubbles preview */}
                  <div className="mt-3 space-y-2" data-id="element-733">
                    <div className="flex gap-2" data-id="element-734">
                      <div className="h-6 w-6 rounded-full bg-sky-500 flex-shrink-0" data-id="element-735"></div>
                      <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded-full w-2/3" data-id="element-736"></div>
                    </div>
                    <div className="flex gap-2 justify-end" data-id="element-737">
                      <div className="h-4 bg-sky-100 dark:bg-sky-900/50 rounded-full w-1/2" data-id="element-738"></div>
                    </div>
                  </div>
                </div>

                <p className="text-slate-600 dark:text-slate-400 text-sm mb-5 leading-relaxed" data-id="element-739">
                  Du hast Favoriten? Ich helfe dir, Bedingungen & Unterschiede
                  in 10–15 Minuten sauber einzuordnen.
                </p>

                <ul className="space-y-3 text-slate-600 dark:text-slate-400 text-sm" data-id="element-740">
                  <li className="flex items-start gap-3" data-id="element-741">
                    <div className="h-5 w-5 rounded-full bg-sky-100 dark:bg-sky-900/30 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-742">
                      <CheckCircle2 className="h-3.5 w-3.5 text-sky-600 dark:text-sky-400" data-id="element-743" />
                    </div>
                    <span data-id="element-744">Welche Klauseln sind wirklich relevant?</span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-745">
                    <div className="h-5 w-5 rounded-full bg-sky-100 dark:bg-sky-900/30 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-746">
                      <CheckCircle2 className="h-3.5 w-3.5 text-sky-600 dark:text-sky-400" data-id="element-747" />
                    </div>
                    <span data-id="element-748">Was ist „Marketing", was ist Substanz?</span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-749">
                    <div className="h-5 w-5 rounded-full bg-sky-100 dark:bg-sky-900/30 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-750">
                      <CheckCircle2 className="h-3.5 w-3.5 text-sky-600 dark:text-sky-400" data-id="element-751" />
                    </div>
                    <span data-id="element-752">Risiken & Lücken verständlich erklärt</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Step 3 */}
            <div className="group relative bg-white dark:bg-slate-900 rounded-2xl overflow-hidden shadow-lg shadow-slate-200/50 dark:shadow-none border border-slate-200/60 dark:border-slate-800 hover:shadow-2xl hover:shadow-slate-500/10 dark:hover:shadow-slate-500/5 hover:-translate-y-2 transition-all duration-500" data-id="element-753">
              {/* Colored accent bar at top */}
              <div className="h-1.5 bg-gradient-to-r from-slate-600 via-slate-500 to-slate-400" data-id="element-754"></div>

              {/* Decorative background number */}
              <div className="absolute top-4 right-4 text-[120px] font-black text-slate-100/50 dark:text-slate-800/30 leading-none select-none pointer-events-none group-hover:text-slate-200/60 dark:group-hover:text-slate-700/30 transition-colors duration-500" data-id="element-755">
                3
              </div>

              <div className="relative p-6" data-id="element-756">
                {/* Icon with gradient background */}
                <div className="mb-5 flex items-center gap-4" data-id="element-757">
                  <div className="relative" data-id="element-758">
                    <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-slate-600 to-slate-700 flex items-center justify-center shadow-lg shadow-slate-500/30 group-hover:shadow-slate-500/50 group-hover:scale-110 transition-all duration-300" data-id="element-759">
                      <FileText className="h-7 w-7 text-white" data-id="element-760" />
                    </div>
                    <div className="absolute -bottom-1 -right-1 h-5 w-5 rounded-full bg-white dark:bg-slate-900 border-2 border-slate-500 flex items-center justify-center" data-id="element-761">
                      <span className="text-[10px] font-bold text-slate-600" data-id="element-762">
                        3
                      </span>
                    </div>
                  </div>
                  <div data-id="element-763">
                    <p className="text-xs font-medium text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-0.5" data-id="element-764">
                      Schritt 3
                    </p>
                    <h3 className="font-bold text-lg text-slate-900 dark:text-white" data-id="element-765">
                      Abschluss
                    </h3>
                  </div>
                </div>

                {/* Mini UI Preview - Enhanced */}
                <div className="relative bg-gradient-to-br from-slate-50 via-white to-slate-100/50 dark:from-slate-800 dark:via-slate-800/80 dark:to-slate-700/20 rounded-xl p-4 mb-5 border border-slate-200 dark:border-slate-700 group-hover:border-slate-400 dark:group-hover:border-slate-600 transition-all duration-300" data-id="element-766">
                  <div className="flex items-center justify-between" data-id="element-767">
                    <div className="flex items-center gap-3" data-id="element-768">
                      <div className="h-8 w-8 rounded-lg bg-slate-200 dark:bg-slate-700 flex items-center justify-center" data-id="element-769">
                        <PenTool className="h-4 w-4 text-slate-600 dark:text-slate-400" data-id="element-770" />
                      </div>
                      <div data-id="element-771">
                        <p className="text-[10px] text-slate-400 uppercase tracking-wide" data-id="element-772">
                          Dokument
                        </p>
                        <p className="text-xs font-medium text-slate-700 dark:text-slate-300" data-id="element-773">
                          Bereit zum Signieren
                        </p>
                      </div>
                    </div>
                  </div>
                  {/* Document preview */}
                  <div className="mt-3 bg-white dark:bg-slate-900 rounded-lg p-2 border border-slate-200 dark:border-slate-700" data-id="element-774">
                    <div className="space-y-1.5" data-id="element-775">
                      <div className="h-1.5 bg-slate-200 dark:bg-slate-700 rounded w-full" data-id="element-776"></div>
                      <div className="h-1.5 bg-slate-200 dark:bg-slate-700 rounded w-4/5" data-id="element-777"></div>
                      <div className="h-1.5 bg-slate-200 dark:bg-slate-700 rounded w-2/3" data-id="element-778"></div>
                    </div>
                    <div className="mt-2 flex justify-end" data-id="element-779">
                      <div className="h-4 w-16 bg-slate-600 rounded text-[8px] text-white flex items-center justify-center font-medium" data-id="element-780">
                        Signieren
                      </div>
                    </div>
                  </div>
                </div>

                <p className="text-slate-600 dark:text-slate-400 text-sm mb-5 leading-relaxed" data-id="element-781">
                  Wenn alles passt, schließen wir sauber ab. Du bekommst
                  Unterlagen und bleibst flexibel.
                </p>

                <ul className="space-y-3 text-slate-600 dark:text-slate-400 text-sm" data-id="element-782">
                  <li className="flex items-start gap-3" data-id="element-783">
                    <div className="h-5 w-5 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-784">
                      <CheckCircle2 className="h-3.5 w-3.5 text-slate-600 dark:text-slate-400" data-id="element-785" />
                    </div>
                    <span data-id="element-786">Unterlagen checken (Bedingungen & Produktinfos)</span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-787">
                    <div className="h-5 w-5 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-788">
                      <CheckCircle2 className="h-3.5 w-3.5 text-slate-600 dark:text-slate-400" data-id="element-789" />
                    </div>
                    <span data-id="element-790">Dokumente gesammelt in der Kunden-App</span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-791">
                    <div className="h-5 w-5 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-792">
                      <CheckCircle2 className="h-3.5 w-3.5 text-slate-600 dark:text-slate-400" data-id="element-793" />
                    </div>
                    <span data-id="element-794">Später anpassen, wenn sich dein Leben ändert</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Section */}
      <BlogSection onNavigate={onNavigate} data-id="element-795" />
    </div>;
}